import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/spinner/Spinner';
import { withModal } from 'components/modal/ModalHOC';

import SOVModalMeta from 'modules/insights/sov/components/modals/meta/SOVModalMeta';

import { __t, applyTranslations } from 'utils/language';
import { FIXED_TO_TOP, INSTAGRAM, REPORT } from 'modules/insights/sov/utils/sov';
import { buildDateString, getReportModifiedDate } from 'modules/insights/sov/utils/dates/sov-dates';

import './sov-header-report.scss';

import type {
  SOVGeneralProps,
  SOVGeneralVoidFunction,
  SOVReport,
  SOVReportType,
  SOVReportUpdateCallback
} from 'modules/insights/sov/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/components/reports/sov-reports.i18n.json'));

type Props = {
  report: SOVReport,
  isReportSaved: boolean,
  isDownloadDisabled: boolean,
  isAnalysisDownloading: boolean,
  onUpdateReport: SOVReportUpdateCallback,
  onDownloadReport: SOVGeneralVoidFunction | undefined | null,
  onClearReportSavedTimer: () => void
};

const DivWithModalMeta = withModal(<div />, {
  modalContent: SOVModalMeta,
  width: 'small',
  positionOverride: FIXED_TO_TOP
});

const COLLABARY_MSG_KEY = 'collabary_common';
const REPORTS_MSG_KEY = 'sov_reports';

class SOVHeaderReport extends React.PureComponent<Props> {
  componentWillUnmount() {
    this.props.onClearReportSavedTimer();
  }

  getReportType(): SOVReportType | undefined | null {
    return this.props.report?.reportType;
  }

  getModalProps(): SOVGeneralProps {
    const { report } = this.props;
    const reportType = this.getReportType();

    return {
      title: __t(`${REPORTS_MSG_KEY}.modal.title_update`, {
        reportType: __t(`${COLLABARY_MSG_KEY}.social.${String(reportType)}`)
      }),
      labels: {
        name: __t(`${REPORTS_MSG_KEY}.labels.report_name`),
        description: __t(`${REPORTS_MSG_KEY}.labels.description`),
        actionButton: __t(`${REPORTS_MSG_KEY}.buttons.done`)
      },
      placeholders: {
        name: __t(`${REPORTS_MSG_KEY}.placeholders.add_name`),
        description: __t(`${REPORTS_MSG_KEY}.placeholders.describe_report`)
      },
      report,
      reportType,
      sectionType: REPORT
    };
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="sov-header-report__spinner">
        <Spinner
          position="inline"
          size="tiny"
          color="light"
        />
      </div>
    );
  }

  renderTooltip(reportModifiedDate: string): React.ReactNode {
    const { report } = this.props;
    const reportCreated = buildDateString('created', report.createdAt);
    const reportUpdated = buildDateString('updated', report.updatedAt);
    const tooltipMsg = `${String(reportCreated)}${!isNil(reportUpdated) ? `<br />${String(reportUpdated)}` : ''}`;

    return (
      <Tooltip message={`${tooltipMsg}`}>
        {reportModifiedDate}
      </Tooltip>
    );
  }

  renderSavedMessage(): React.ReactNode {
    const { isReportSaved } = this.props;
    const reportType = this.getReportType();

    const savedClasses = classnames('sov-header-report__saved', {
      'sov-header-report__saved--is-saved': isReportSaved,
      'sov-header-report__saved--has-button': reportType === INSTAGRAM
    });

    return (
      <div className={savedClasses}>
        {__t(`${REPORTS_MSG_KEY}.labels.saved`)}
      </div>
    );
  }

  renderDownloadButton(): React.ReactNode | undefined | null {
    const { isDownloadDisabled, isAnalysisDownloading, onDownloadReport } = this.props;
    const reportType = this.getReportType();

    if (reportType !== INSTAGRAM) {
      return null;
    }

    return (
      <div className="sov-header-report__download">
        <ActionButton
          disabled={isDownloadDisabled || isAnalysisDownloading}
          onClick={onDownloadReport || noop}
        >
          {__t(`${REPORTS_MSG_KEY}.buttons.download`)}
        </ActionButton>
        {
          isAnalysisDownloading &&
            this.renderSpinner()
        }
      </div>
    );
  }

  renderReportInfo(): React.ReactNode {
    const { report } = this.props;
    const reportName = report.name;
    const reportDescription = report.description;

    return (
      <div className="sov-header-report__info">
        <div
          className="sov-header-report__name text-overflow-ellipsis"
          title={reportName}
        >
          {reportName}
        </div>
        {
          reportDescription && (
            <div
              className="sov-header-report__description text-overflow-ellipsis"
              title={reportDescription}
            >
              {reportDescription}
            </div>
          )
        }
      </div>
    );
  }

  renderUtil(): React.ReactNode {
    const { report } = this.props;
    const reportModifiedDate = getReportModifiedDate(report.createdAt, report.updatedAt);

    return (
      <div className="sov-header-report__util">
        <div className="sov-header-report__util-info">
          {this.renderSavedMessage()}
          {this.renderDownloadButton()}
        </div>
        {
          reportModifiedDate && (
            <div className="sov-header-report__util-tooltip">
              {this.renderTooltip(reportModifiedDate)}
            </div>
          )
        }
      </div>
    );
  }

  renderMeta(): React.ReactNode {
    const { onUpdateReport } = this.props;

    return (
      <DivWithModalMeta
        modalContentProps={{
          ...this.getModalProps(),
          onAction: onUpdateReport
        }}
      >
        {this.renderReportInfo()}
      </DivWithModalMeta>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="sov-header-report">
        {this.renderMeta()}
        {this.renderUtil()}
      </div>
    );
  }
}

export default SOVHeaderReport;
